import React from 'react';
import { Drawer as VaulDrawer } from 'vaul';
import cn from 'classnames';
import { Button, ButtonProps } from '@base/lib/Button';
import { IconArrow, IconX } from '@base/icon';

type ICloseIconType = 'x' | 'arrow';

type DrawerHeaderProps = {
    title?: React.ReactNode;
    extra?: React.ReactNode;
    closeIcon?: boolean | React.ReactNode;
    closeIconType?: ICloseIconType,
    closePlacement?: 'left' | 'right';
    titleVisible?: boolean; //标题是否可见
}

type DrawerBodyProps = {
    className?: string;
    children?: React.ReactNode;
}

type DrawerFooterProps = {
    cancelText?: React.ReactNode;
    children?: boolean | React.ReactNode;
}

export type DrawerProps = DrawerHeaderProps & DrawerFooterProps & {
    className?: string;
    style?: React.CSSProperties;
    width?: number | string;
    height?: number | string; //高度, 在 placement 为 top 或 bottom 时使用
    placement?: 'top' | 'right' | 'bottom' | 'left';

    trigger?: React.ReactNode;

    header?: boolean | React.ReactNode; //为 false 时不渲染默认 header
    body?: boolean | React.ReactNode; //为 false 时不渲染默认 DrawerBody
    footer?: boolean | React.ReactNode; //为 false 时不渲染默认 footer

    cancelText?: React.ReactNode;
    children?: React.ReactNode;

    mask?: boolean;
    maskClosable?: boolean;
    maskClassName?: string;
    maskStyle?: React.CSSProperties;

    open?: boolean;
    onOpenChange?: (open: boolean) => void; //对应 antd afterOpenChange
    onClose?: () => void;

    defaultOpen?: boolean;
    dragClosable?: boolean; // 拖拽关闭抽屉
    closable?: boolean; // When false dragging, clicking outside, pressing esc, etc. will not close the drawer. Use this in combination with the open prop, otherwise you won't be able to open/close the drawer.

    /** Wrapper dom node style of header and body */
    // drawerStyle?: React.CSSProperties;
    // headerStyle?: React.CSSProperties;
    // bodyStyle?: React.CSSProperties;
    // footerStyle?: React.CSSProperties;
};

export const DrawerWrapper: Globals.FC = ({ className, ...props }) => {
    return (
        <div
            className={cn(className, 'sw-drawer-wrapper flex-1')}
            {...props}
        />
    )
};

export const DrawerHeader: Globals.FC<DrawerHeaderProps> = props => {
    const {
        className,
        children,
        title,
        titleVisible = true,
        extra,
        closeIcon = true,
        closeIconType = 'x',
        closePlacement = 'right',
    } = props;

    if ((!title || (!!title && !titleVisible)) && !extra && !closeIcon && (!children || children === true)) {
        if (!!title && !titleVisible) {
            return (
                <DrawerTitle visible={false}>{title}</DrawerTitle>
            );
        }
        return null;
    }

    return (
        <div
            className={cn(
                className,
                'sw-drawer-header sticky top-0 bg-inherit flex flex-middle z-index-1',
                { 'flex-reverse': closePlacement === 'right' }
            )}
        >
            {!!children && children !== true ? children : (
                <>
                    {/*宽度占位*/}
                    {(!closeIcon && !!extra) && <DrawerHeaderWidget/>}

                    {closeIcon && (
                        <DrawerClose>
                            <DrawerHeaderButton>
                                {typeof closeIcon !== 'boolean'
                                    ? closeIcon
                                    : (closeIconType === 'x' ? <IconX size={false}/> : <IconArrow direction={'left'}/>)
                                }
                            </DrawerHeaderButton>
                        </DrawerClose>
                    )}

                    {!!title && <DrawerTitle visible={titleVisible}>{title}</DrawerTitle>}

                    {extra}

                    {/*宽度占位*/}
                    {closeIcon && !extra && <DrawerHeaderWidget/>}
                </>
            )}
        </div>
    )
};

export const DrawerTitle: Globals.FC<{ visible?: boolean; }> = ({ children, visible = true }) => {
    return (
        <VaulDrawer.Title className={cn('sw-drawer-title flex-1', { 'visually-hidden': !visible })}>
            {children}
        </VaulDrawer.Title>
    )
};

export const DrawerClose: Globals.FC = ({ children }) => {
    return (
        <VaulDrawer.Close asChild>
            {/* Fragment 用于消除 VaulDrawer.Close 传递到 children 的 type 属性，不能去掉 */}
            {children}
        </VaulDrawer.Close>
    )
};

export const DrawerHeaderWidget: Globals.FC = ({ className, children }) => {
    return (
        <div className={cn(className, 'sw-drawer-header-widget h-full')}>
            {children}
        </div>
    )
};

export const DrawerHeaderButton = React.forwardRef<HTMLDivElement, ButtonProps>(({ className, type, ...props }, ref) => {
    // 为了防止上级组件传递的 type 覆盖了本组件的 type，需要把props中的 type 去掉
    return (
        <Button
            {...props}
            ref={ref as React.Ref<HTMLButtonElement>}
            className={cn(className, 'sw-drawer-header-btn h-full')}
            type={'no-style'}
        />
    )
});

export const DrawerFooter: Globals.FC<DrawerFooterProps> = ({ className, cancelText, children }) => {
    return (!children && !cancelText) || (children === true && !cancelText) ? null : (
        <div className={cn(className, 'sw-drawer-footer sticky bottom-0 bg-inherit z-index-1')}>
            {!!children && children !== true ? children : (
                <DrawerClose>
                    <DrawerFooterButton>{cancelText}</DrawerFooterButton>
                </DrawerClose>
            )}
        </div>
    )
};

export const DrawerFooterButton: Globals.FC<ButtonProps & { text?: React.ReactNode; }> = ({ className, ...props }) => {
    return (
        <Button
            {...props}
            type={'primary'}
            block
        />
    )
};

export const DrawerBody = React.forwardRef<HTMLDivElement, DrawerBodyProps>((props, ref) => {
    const { className, children } = props;
    return (
        <VaulDrawer.Description asChild>
            <div ref={ref} className={cn(className, 'sw-drawer-body relative z-index-0')}>
                {children}
            </div>
        </VaulDrawer.Description>
    )
});

export const Drawer: Globals.FC<DrawerProps> = ({ className, style, children, ...props }) => {
    const {
        width,
        height,
        placement = 'bottom',
        trigger,
        header = true,
        body,
        footer = true,
        cancelText,

        mask = true,
        maskClosable = true,
        maskClassName,
        maskStyle,

        open,
        onOpenChange,
        onClose,

        defaultOpen = false,
        dragClosable = false,
        closable = true,
    } = props;

    // 默认显示关闭按钮图标，左侧滑出默认左箭头，其他默认x
    let { closeIcon, closeIconType, closePlacement } = props;
    // 禁止关闭弹窗时，默认不显示关闭按钮
    if (!closable && typeof closeIcon === 'undefined') {
        closeIcon = false;
    }
    if (placement === 'left' && closeIcon !== false && !closeIconType) {
        closeIconType = 'arrow';
    }
    if (placement === 'left' && typeof closePlacement === 'undefined') {
        closePlacement = 'left';
    }

    return (
        <VaulDrawer.Root
            open={open}
            onOpenChange={(open) => {
                onOpenChange?.(open);
                if (!open) {
                    onClose?.();
                }
            }}
            direction={placement}
            handleOnly={!dragClosable}
            defaultOpen={defaultOpen}
            dismissible={closable}
        >
            {!!trigger && <VaulDrawer.Trigger asChild>{trigger}</VaulDrawer.Trigger>}

            <VaulDrawer.Portal>
                {mask && (
                    <VaulDrawer.Overlay
                        className={cn(maskClassName, 'sw-drawer-mask fixed inset-0')}
                        style={maskStyle}
                    />
                )}

                <VaulDrawer.Content
                    onPointerDownOutside={event => {
                        if (!maskClosable) {
                            event.preventDefault();
                        }
                    }}
                    className={cn(className, 'sw-drawer', `sw-drawer-${placement}`, 'flex flex-vertical')}
                    style={{
                        ...style,
                        width: width || (['left', 'right'].includes(placement) ? '90%' : void 0),
                        maxHeight: height || (['top', 'bottom'].includes(placement) ? '90%' : void 0),
                    }}
                >
                    <DrawerWrapper>
                        {header && (
                            <DrawerHeader
                                {...props}
                                closeIcon={closable ? closeIcon : false}
                                closeIconType={closeIconType}
                                closePlacement={closePlacement}
                            >
                                {header}
                            </DrawerHeader>
                        )}

                        {body ? <DrawerBody>{body}</DrawerBody> : children}

                        {footer && <DrawerFooter cancelText={cancelText}>{footer}</DrawerFooter>}
                    </DrawerWrapper>
                </VaulDrawer.Content>
            </VaulDrawer.Portal>
        </VaulDrawer.Root>
    );
};